import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, forkJoin, Observable, of, Subject, Subscription, switchMap } from 'rxjs';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { AuthService } from '../../../core/auth/auth.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { AppointmentsService } from '../data-access/appointments.service';
import {
  AppointmentListItem,
  AppointmentListPageItem,
  AppointmentListSort,
  AppointmentStatus,
} from '../data-access/appointments.models';
import { AppointmentsTableComponent } from '../components/appointments-table.component';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import {
  ErrorBlockComponent,
  FloatingActionFabComponent,
  ListFilterShortcutDirective,
  ModalKeyboardDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';
import { AppointmentCreateFlowComponent } from '../components/appointment-create-flow.component';
import { AppointmentFormPrefill } from '../components/appointment-form.component';
import { AppointmentEditPageComponent } from './appointment-edit-page.component';
import { AppointmentQuickViewModalComponent } from '../components/appointment-quick-view-modal.component';
import { UserProfilePageComponent } from '../../users/pages/user-profile-page.component';
import { DeviceQuickViewModalComponent } from '../../devices/components/device-quick-view-modal.component';
import { DevicesService } from '../../devices/data-access/devices.service';
import { DeviceItem } from '../../devices/data-access/devices.models';
import { ClinicQuickViewModalComponent } from '../../clinics/components/clinic-quick-view-modal.component';
import { ProcedureTypeQuickViewModalComponent } from '../../procedure-types/components/procedure-type-quick-view-modal.component';
import { ProcedureTypesService } from '../../procedure-types/data-access/procedure-types.service';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { SpecialistQuickViewModalComponent } from '../../specialists/components/specialist-quick-view-modal.component';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { UsersService } from '../../users/data-access/users.service';
import { UserItem } from '../../users/data-access/users.models';
import { ClientsAdminService } from '../../clients/data-access/clients-admin.service';
import { ClientAdminItem } from '../../clients/data-access/clients-admin.models';

interface AppointmentFilterOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-appointments-list-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    AppointmentsTableComponent,
    CompanyContextRequiredComponent,
    ErrorBlockComponent,
    FloatingActionFabComponent,
    ListFilterShortcutDirective,
    ModalKeyboardDirective,
    PageActionModalComponent,
    PageContextGuardComponent,
    PageShellComponent,
    PagedFeedComponent,
    AppointmentCreateFlowComponent,
    AppointmentEditPageComponent,
    AppointmentQuickViewModalComponent,
    DeviceQuickViewModalComponent,
    ClinicQuickViewModalComponent,
    ProcedureTypeQuickViewModalComponent,
    SpecialistQuickViewModalComponent,
    UserProfilePageComponent,
    TranslatePipe
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      [libListFilterShortcut]="!companyContextMissing && !isClient"
      (libListFilterShortcutOpen)="openFiltersModal()"
    >
      <app-page-header
        title="Appointments"
        subtitle="Operational view of current appointments"
      />

      <lib-page-context-guard [blocked]="companyContextMissing">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!companyContextMissing) {
        @if (actionErrorText) {
          <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
        }

        <lib-paged-feed
          [items]="items"
          [collectionTemplate]="appointmentsTableTemplate"
          mode="manual"
          [hasMore]="hasMoreAppointments"
          [loading]="isLoading"
          [loadingMore]="isLoadingMore"
          [errorText]="errorText ? ((errorText | t) ?? '') : null"
          [loadingText]="'Loading appointments...' | t"
          [emptyText]="'No appointments found' | t"
          [retryText]="'Retry' | t"
          [loadMoreText]="'Load more appointments' | t"
          [loadingMoreText]="'Loading more...' | t"
          (loadMore)="loadMoreAppointments()"
          (retry)="retryAppointments()"
        />
      }

      @if (!companyContextMissing && hasPageActions) {
        <lib-floating-action-fab
          [menuTemplate]="appointmentsPageActionsMenu"
          buttonAriaLabel="Open appointment page actions"
        />

        <ng-template #appointmentsPageActionsMenu let-close="close">
          <div class="appointments-page__actions-menu">
            @if (canCreateAppointment) {
              <button
                type="button"
                class="action-menu__item action-menu__item--primary"
                (click)="openCreateModal(); close()"
              >
                {{ 'Create Appointment' | t }}
              </button>
            }

            @if (!isClient) {
              <button
                type="button"
                class="action-menu__item"
                (click)="openFiltersModal(); close()"
              >
                {{ 'Filters & Sort' | t }} (Ctrl+F)
              </button>
            }

            <button
              type="button"
              class="action-menu__item action-menu__item--subtle"
              (click)="refreshAppointments(); close()"
            >
              {{ 'Refresh' | t }}
            </button>
          </div>
        </ng-template>
      }

      <ng-template #appointmentsTableTemplate let-feedItems>
        <app-appointments-table
          [items]="feedItems"
          [busyAppointmentId]="busyAppointmentId"
          [canConfirmOrCancel]="canConfirmOrCancel"
          [canComplete]="canComplete"
          [isClientActor]="isClient"
          [canOpenClient]="canOpenClient"
          [canOpenDevice]="canOpenDevice"
          [canOpenClinic]="canOpenClinic"
          [canOpenSpecialist]="canOpenSpecialist"
          [canOpenProcedure]="canOpenProcedure"
          (clientOpenClicked)="onClientOpen($event)"
          (deviceOpenClicked)="onDeviceOpen($event)"
          (clinicOpenClicked)="onClinicOpen($event)"
          (specialistOpenClicked)="onSpecialistOpen($event)"
          (procedureOpenClicked)="onProcedureOpen($event)"
          (editClicked)="onEdit($event)"
          (checkInClicked)="onCheckIn($event)"
          (confirmClicked)="onConfirm($event)"
          (cancelClicked)="onCancel($event)"
          (completeClicked)="onComplete($event)"
          (noShowClicked)="onNoShow($event)"
        />
      </ng-template>

      <lib-page-action-modal
        [isOpen]="isFiltersModalOpen"
        [title]="('Filters & Sort' | t) ?? 'Filters & Sort'"
        [subtitle]="('Appointment list query controls' | t) ?? 'Appointment list query controls'"
        width="520px"
        closeAriaLabel="Close appointment filters modal"
        (close)="closeFiltersModal()"
      >
            <form class="appointments-page__filters-form" (submit)="$event.preventDefault(); applyFiltersModal()">
              @if (filterOptionsLoading) {
                <div class="appointments-page__filter-hint">{{ 'Loading filters...' | t }}</div>
              }

              <label class="appointments-page__filter">
                <span>{{ 'common.status' | t }}</span>
                <select
                  class="appointments-page__select"
                  [value]="draftStatus"
                  (change)="onDraftStatusChange($event)"
                >
                  <option value="">{{ 'appointments.filters.allStatuses' | t }}</option>
                  <option value="pending_confirmation">{{ 'status.pendingConfirmation' | t }}</option>
                  <option value="confirmed">{{ 'status.confirmed' | t }}</option>
                  <option value="completed">{{ 'status.completed' | t }}</option>
                  <option value="cancelled">{{ 'status.cancelled' | t }}</option>
                  <option value="reschedule_required">{{ 'status.rescheduleRequired' | t }}</option>
                  <option value="no_show">{{ 'status.noShow' | t }}</option>
                </select>
              </label>

              <label class="appointments-page__filter">
                <span>{{ 'Clinic' | t }}</span>
                <select
                  class="appointments-page__select"
                  [value]="draftClinicId"
                  (change)="onDraftClinicChange($event)"
                >
                  <option value="">{{ 'All' | t }}</option>
                  @for (clinic of clinicOptions; track clinic.id) {
                    <option [value]="clinic.id">{{ clinic.label }}</option>
                  }
                </select>
              </label>

              <label class="appointments-page__filter">
                <span>{{ 'Client' | t }}</span>
                <input
                  class="appointments-page__select"
                  [value]="draftClientSearchText"
                  list="appointments-client-filter-options"
                  placeholder="Search client"
                  data-modal-primary-focus="true"
                  (input)="onClientSearchInput($event)"
                  (change)="onClientSearchCommitted($event)"
                  (keyup.enter)="onClientSearchCommitted($event)"
                />
                <datalist id="appointments-client-filter-options">
                  @for (client of clientOptions; track client.id) {
                    <option [value]="clientOptionLabel(client)"></option>
                  }
                </datalist>
              </label>

              <label class="appointments-page__filter">
                <span>{{ 'Specialist' | t }}</span>
                <select
                  class="appointments-page__select"
                  [value]="draftSpecialistUserId"
                  (change)="onDraftSpecialistChange($event)"
                >
                  <option value="">{{ 'All' | t }}</option>
                  @for (specialist of specialistOptions; track specialist.id) {
                    <option [value]="specialist.id">{{ specialist.label }}</option>
                  }
                </select>
              </label>

              <label class="appointments-page__filter">
                <span>{{ 'Procedure' | t }}</span>
                <select
                  class="appointments-page__select"
                  [value]="draftProcedureTypeId"
                  (change)="onDraftProcedureChange($event)"
                >
                  <option value="">{{ 'All' | t }}</option>
                  @for (procedure of procedureTypeOptions; track procedure.id) {
                    <option [value]="procedure.id">{{ procedure.label }}</option>
                  }
                </select>
              </label>

              <label class="appointments-page__filter">
                <span>{{ 'Device' | t }}</span>
                <select
                  class="appointments-page__select"
                  [value]="draftDeviceId"
                  (change)="onDraftDeviceChange($event)"
                >
                  <option value="">{{ 'All' | t }}</option>
                  @for (device of deviceOptions; track device.id) {
                    <option [value]="device.id">{{ device.label }}</option>
                  }
                </select>
              </label>

              <label class="appointments-page__filter">
                <span>{{ 'appointments.filters.sort' | t }}</span>
                <select
                  class="appointments-page__select"
                  [value]="draftSort"
                  (change)="onDraftSortChange($event)"
                >
                  <option value="startAtDesc">{{ 'appointments.sort.startAtDesc' | t }}</option>
                  <option value="startAtAsc">{{ 'appointments.sort.startAtAsc' | t }}</option>
                  <option value="createdAtDesc">{{ 'appointments.sort.createdAtDesc' | t }}</option>
                  <option value="createdAtAsc">{{ 'appointments.sort.createdAtAsc' | t }}</option>
                </select>
              </label>

              <label class="appointments-page__checkbox-filter">
                <input
                  type="checkbox"
                  [checked]="draftPendingFirst"
                  (change)="onDraftPendingFirstChange($event)"
                />
                <span>{{ 'Pending first' | t }}</span>
              </label>

              <div class="appointments-page__modal-actions">
                <button
                  type="button"
                  class="appointments-page__secondary-button"
                  (click)="resetFiltersModal()"
                >
                  {{ 'Reset' | t }}
                </button>

                <button
                  type="submit"
                  class="appointments-page__primary-button"
                >
                  {{ 'Apply' | t }}
                </button>
              </div>
            </form>
      </lib-page-action-modal>

      @if (isCreateAppointmentModalOpen) {
        <div class="appointments-page__modal-backdrop" (click)="closeCreateModal()">
          <div
            class="appointments-page__modal"
            (click)="$event.stopPropagation()"
            [libModalKeyboard]="true"
            [libModalKeyboardCanSubmit]="false"
            (libModalKeyboardCancel)="closeCreateModal()"
          >
            <div class="appointments-page__modal-head">
              <div>
                <h3 class="appointments-page__modal-title">{{ 'Create Appointment' | t }}</h3>
                <p class="appointments-page__modal-subtitle">
                  {{ 'Manual operational creation form' | t }}
                </p>
              </div>

              <button
                type="button"
                class="appointments-page__modal-close"
                (click)="closeCreateModal()"
                aria-label="Close create appointment modal"
              >
                ×
              </button>
            </div>

            <app-appointment-create-flow
              [prefill]="createAppointmentPrefill"
              [navigateOnSuccess]="false"
              (created)="handleAppointmentCreated()"
            />
          </div>
        </div>
      }

      @if (isEditAppointmentModalOpen && editAppointmentId) {
        <div class="appointments-page__modal-backdrop" (click)="closeEditModal()">
          <div
            class="appointments-page__modal appointments-page__modal--edit"
            (click)="$event.stopPropagation()"
            [libModalKeyboard]="true"
            [libModalKeyboardCanSubmit]="false"
            (libModalKeyboardCancel)="closeEditModal()"
          >
            <div class="appointments-page__modal-head">
              <div>
                <h3 class="appointments-page__modal-title">{{ 'Edit appointment' | t }}</h3>
                <p class="appointments-page__modal-subtitle">
                  {{ 'Request a different appointment time without leaving the schedule.' | t }}
                </p>
              </div>

              <button
                type="button"
                class="appointments-page__modal-close"
                (click)="closeEditModal()"
                aria-label="Close appointment edit modal"
              >
                ×
              </button>
            </div>

            <app-appointment-edit-page
              [appointmentIdOverride]="editAppointmentId"
              [modalPresentation]="true"
              [navigateOnSuccess]="false"
              (saved)="handleAppointmentEditSaved()"
            />
          </div>
        </div>
      }

      @if (isClientProfileModalOpen && selectedClientProfileId) {
        <div class="appointments-page__modal-backdrop" (click)="closeClientProfileModal()">
          <div
            class="appointments-page__modal appointments-page__modal--client"
            (click)="$event.stopPropagation()"
            [libModalKeyboard]="true"
            [libModalKeyboardCanSubmit]="false"
            (libModalKeyboardCancel)="closeClientProfileModal()"
          >
            <div class="appointments-page__modal-head">
              <div>
                <h3 class="appointments-page__modal-title">{{ 'Client Profile' | t }}</h3>
                <p class="appointments-page__modal-subtitle">
                  {{ 'CRM client context without leaving the current appointment view.' | t }}
                </p>
              </div>

              <button
                type="button"
                class="appointments-page__modal-close"
                (click)="closeClientProfileModal()"
                aria-label="Close client profile modal"
              >
                ×
              </button>
            </div>

            <app-user-profile-page
              [clientIdOverride]="selectedClientProfileId"
              presentationMode="quick-view"
            />
          </div>
        </div>
      }

      @if (isDeviceQuickViewOpen && selectedDeviceId) {
        <app-device-quick-view-modal
          [isOpen]="true"
          [deviceId]="selectedDeviceId"
          title="Device Quick View"
          (close)="closeDeviceQuickView()"
        />
      }

      @if (isClinicQuickViewOpen && selectedClinicId) {
        <app-clinic-quick-view-modal
          [isOpen]="true"
          [clinicId]="selectedClinicId"
          title="Clinic Quick View"
          (close)="closeClinicQuickView()"
        />
      }

      @if (isProcedureQuickViewOpen && selectedProcedureTypeId) {
        <app-procedure-type-quick-view-modal
          [isOpen]="true"
          [procedureTypeId]="selectedProcedureTypeId"
          title="Procedure Type Quick View"
          (close)="closeProcedureQuickView()"
        />
      }

      @if (isSpecialistQuickViewOpen && selectedSpecialistUserId) {
        <app-specialist-quick-view-modal
          [isOpen]="true"
          [specialistUserId]="selectedSpecialistUserId"
          title="Specialist Quick View"
          (close)="closeSpecialistQuickView()"
        />
      }

      @if (isAppointmentQuickViewOpen && selectedAppointmentQuickViewId) {
        <app-appointment-quick-view-modal
          [isOpen]="true"
          [appointmentId]="selectedAppointmentQuickViewId"
          [clientViewMode]="selectedAppointmentClientViewMode"
          title="Appointment Quick View"
          (editRequested)="openEditFromQuickView($event)"
          (close)="closeAppointmentQuickView()"
        />
      }
    </lib-page-shell>
  `,
  styles: [`
    .appointments-page__actions-menu {
      display: grid;
      gap: 4px;
    }

    .appointments-page__primary-button,
    .appointments-page__secondary-button {
      height: 40px;
      border-radius: 8px;
      padding: 0 16px;
      cursor: pointer;
      white-space: nowrap;
      font: inherit;
      font-weight: 700;
    }

    .appointments-page__primary-button {
      border: none;
      background: #2563eb;
      color: #ffffff;
    }

    .appointments-page__secondary-button {
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #334155;
    }

    .appointments-page__filters-form {
      display: grid;
      gap: 16px;
    }

    .appointments-page__filter-hint {
      color: #64748b;
      font-size: 13px;
      font-weight: 700;
    }

    .appointments-page__filter {
      display: grid;
      gap: 6px;
      color: #475569;
      font-size: 12px;
      font-weight: 700;
    }

    .appointments-page__checkbox-filter {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #475569;
      font-size: 13px;
      font-weight: 700;
    }

    .appointments-page__select {
      width: 100%;
      height: 38px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #ffffff;
      color: #0f172a;
      padding: 0 10px;
      font: inherit;
      font-weight: 500;
    }

    .appointments-page__modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 4px;
    }

    .appointments-page__modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(6px);
    }

    .appointments-page__modal {
      width: min(1040px, 100%);
      max-height: min(90vh, 960px);
      overflow: auto;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.97));
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
      padding: 22px;
      box-sizing: border-box;
    }

    .appointments-page__modal-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    .appointments-page__modal-title {
      margin: 0;
      font-size: 24px;
      line-height: 1.1;
      color: #111827;
    }

    .appointments-page__modal-subtitle {
      margin: 8px 0 0;
      color: #6b7280;
      font-size: 14px;
      line-height: 1.5;
    }

    .appointments-page__modal-close {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: 1px solid #dbe2ea;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      color: #475569;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }

    @media (max-width: 640px) {
      .appointments-page__modal-backdrop {
        padding: 0;
        align-items: flex-end;
      }

      .appointments-page__modal {
        width: 100%;
        max-height: 92vh;
        border-radius: 24px 24px 0 0;
        padding: 16px;
      }
    }

    .appointments-page__modal--edit {
      width: min(1120px, 100%);
    }

    .appointments-page__modal--client {
      width: min(980px, 100%);
    }

    @media (max-width: 640px) {
      .appointments-page__modal-actions {
        display: grid;
      }

      .appointments-page__primary-button,
      .appointments-page__secondary-button {
        width: 100%;
      }
    }

  `]
})
export class AppointmentsListPageComponent implements OnInit, OnDestroy
{
  private static readonly PAGE_SIZE = 20;
  private static readonly CLIENT_SEARCH_LIMIT = 10;

  private readonly appointmentsService = inject(AppointmentsService);
  private readonly clientsAdminService = inject(ClientsAdminService);
  private readonly devicesService = inject(DevicesService);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly settingsService = inject(SettingsService);
  private readonly usersService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly subscriptions = new Subscription();
  private readonly clientSearchTerms = new Subject<string>();
  private queryStateInitialized = false;
  private filterOptionsLoadedForCompanyId: string | null = null;
  protected filterOptionsLoading = false;

  protected items: AppointmentListItem[] = [];
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreAppointments = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected busyAppointmentId: string | null = null;
  protected canCreateAppointment = false;
  protected canConfirmOrCancel = false;
  protected canComplete = false;
  protected isClient = false;
  protected canOpenClient = false;
  protected canOpenDevice = false;
  protected canOpenClinic = false;
  protected canOpenSpecialist = false;
  protected canOpenProcedure = false;
  protected isSystemAdmin = false;
  protected isCreateAppointmentModalOpen = false;
  protected isEditAppointmentModalOpen = false;
  protected isFiltersModalOpen = false;
  protected isClientProfileModalOpen = false;
  protected isDeviceQuickViewOpen = false;
  protected isClinicQuickViewOpen = false;
  protected isProcedureQuickViewOpen = false;
  protected isSpecialistQuickViewOpen = false;
  protected isAppointmentQuickViewOpen = false;
  protected editAppointmentId: string | null = null;
  protected selectedAppointmentQuickViewId: string | null = null;
  protected selectedAppointmentClientViewMode = false;
  protected selectedClientProfileId: string | null = null;
  protected selectedDeviceId: string | null = null;
  protected selectedClinicId: string | null = null;
  protected selectedProcedureTypeId: string | null = null;
  protected selectedSpecialistUserId: string | null = null;
  protected createAppointmentPrefill: AppointmentFormPrefill = {};
  protected nextCursor: string | null = null;
  protected selectedStatus: '' | AppointmentStatus = '';
  protected selectedSort: AppointmentListSort = 'startAtDesc';
  protected selectedPendingFirst = true;
  protected selectedClinicFilterId = '';
  protected selectedClientId = '';
  protected selectedDeviceIdFilter = '';
  protected selectedSpecialistUserIdFilter = '';
  protected selectedProcedureTypeIdFilter = '';
  protected draftStatus: '' | AppointmentStatus = '';
  protected draftSort: AppointmentListSort = 'startAtDesc';
  protected draftPendingFirst = true;
  protected draftClinicId = '';
  protected draftClientId = '';
  protected draftDeviceId = '';
  protected draftSpecialistUserId = '';
  protected draftProcedureTypeId = '';
  protected clientSearchText = '';
  protected draftClientSearchText = '';
  protected clientOptions: ClientAdminItem[] = [];
  protected clinicOptions: AppointmentFilterOption[] = [];
  protected deviceOptions: AppointmentFilterOption[] = [];
  protected specialistOptions: AppointmentFilterOption[] = [];
  protected procedureTypeOptions: AppointmentFilterOption[] = [];

  public ngOnInit(): void
  {
    const role = this.authService.getCurrentRole();
    this.bindClientSearch();
    this.isSystemAdmin = role === 'system_admin';
    this.isClient = role === 'client';
    this.canCreateAppointment =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'reception' ||
      role === 'client';
    this.canConfirmOrCancel =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'reception' ||
      role === 'client';
    this.canComplete =
      role === 'system_admin' || role === 'company_admin' || role === 'manager' || role === 'technician';
    this.canOpenClient =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager';
    this.canOpenDevice =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'reception' ||
      role === 'client';
    this.canOpenClinic = this.canOpenDevice;
    this.canOpenSpecialist =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'client';
    this.canOpenProcedure = true;

    if (this.isSystemAdmin) {
      this.subscriptions.add(
        combineLatest([
          this.route.queryParamMap,
          this.systemAdminContext.companyId$.pipe(distinctUntilChanged()),
        ]).subscribe(([params]) => {
          this.applyAppointmentQueryState(params);
          this.applyAppointmentQuickViewQueryState(params);
          this.applyAppointmentEditQueryState(params);
          this.loadAppointments({ append: false });
        }),
      );
      return;
    }

    this.subscriptions.add(
      this.route.queryParamMap.subscribe((params) => {
        const changed = this.applyAppointmentQueryState(params);
        this.applyAppointmentQuickViewQueryState(params);
        this.applyAppointmentEditQueryState(params);

        if (!this.queryStateInitialized) {
          this.queryStateInitialized = true;
          this.loadAppointments({ append: false });
          return;
        }

        if (!this.isClient && changed) {
          this.loadAppointments({ append: false });
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected openCreateModal(): void
  {
    this.createAppointmentPrefill = {};
    this.isCreateAppointmentModalOpen = true;
  }

  protected closeCreateModal(): void
  {
    this.isCreateAppointmentModalOpen = false;
  }

  protected handleAppointmentCreated(): void
  {
    this.isCreateAppointmentModalOpen = false;
    this.loadAppointments({ append: false });
  }

  protected onConfirm(item: AppointmentListItem): void
  {
    this.busyAppointmentId = item.id;
    this.actionErrorText = null;

    this.appointmentsService.confirm(item.id).subscribe({
      next: () =>
      {
        this.busyAppointmentId = null;
        this.loadAppointments({ append: false });
      },
      error: (error: unknown) =>
      {
        console.error('Failed to confirm appointment', error);
        this.busyAppointmentId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected onEdit(item: AppointmentListItem): void
  {
    this.editAppointmentId = item.id;
    this.isEditAppointmentModalOpen = true;
  }

  protected closeEditModal(): void
  {
    this.isEditAppointmentModalOpen = false;
    this.editAppointmentId = null;
    this.clearAppointmentEditQueryParam();
  }

  protected handleAppointmentEditSaved(): void
  {
    this.closeEditModal();
    this.loadAppointments({ append: false });
  }

  protected onClientOpen(clientId: string): void
  {
    if (!this.canOpenClient || !clientId.trim()) {
      return;
    }

    this.selectedClientProfileId = clientId.trim();
    this.isClientProfileModalOpen = true;
  }

  protected closeClientProfileModal(): void {
    this.isClientProfileModalOpen = false;
    this.selectedClientProfileId = null;
  }

  protected onDeviceOpen(deviceId: string): void
  {
    if (!this.canOpenDevice || !deviceId.trim()) {
      return;
    }

    this.selectedDeviceId = deviceId.trim();
    this.isDeviceQuickViewOpen = true;
  }

  protected closeDeviceQuickView(): void
  {
    this.isDeviceQuickViewOpen = false;
    this.selectedDeviceId = null;
  }

  protected onClinicOpen(clinicId: string): void
  {
    if (!this.canOpenClinic || !clinicId.trim()) {
      return;
    }

    this.selectedClinicId = clinicId.trim();
    this.isClinicQuickViewOpen = true;
  }

  protected closeClinicQuickView(): void
  {
    this.isClinicQuickViewOpen = false;
    this.selectedClinicId = null;
  }

  protected onSpecialistOpen(specialistUserId: string): void
  {
    if (!this.canOpenSpecialist || !specialistUserId.trim()) {
      return;
    }

    this.selectedSpecialistUserId = specialistUserId.trim();
    this.isSpecialistQuickViewOpen = true;
  }

  protected closeSpecialistQuickView(): void
  {
    this.isSpecialistQuickViewOpen = false;
    this.selectedSpecialistUserId = null;
  }

  protected closeAppointmentQuickView(): void
  {
    this.isAppointmentQuickViewOpen = false;
    this.selectedAppointmentQuickViewId = null;
    this.selectedAppointmentClientViewMode = false;
    this.clearAppointmentQuickViewQueryParam();
  }

  protected openEditFromQuickView(appointmentId: string): void
  {
    const normalized = appointmentId.trim();
    if (!normalized) {
      return;
    }

    this.isAppointmentQuickViewOpen = false;
    this.selectedAppointmentQuickViewId = null;
    this.selectedAppointmentClientViewMode = false;
    this.editAppointmentId = normalized;
    this.isEditAppointmentModalOpen = true;
    this.clearAppointmentQuickViewQueryParam();
  }

  protected onProcedureOpen(procedureTypeId: string): void
  {
    const normalized = procedureTypeId.trim();
    if (!this.canOpenProcedure || !normalized) {
      return;
    }

    this.selectedProcedureTypeId = normalized;
    this.isProcedureQuickViewOpen = true;
  }

  protected closeProcedureQuickView(): void
  {
    this.isProcedureQuickViewOpen = false;
    this.selectedProcedureTypeId = null;
  }

  protected onCancel(item: AppointmentListItem): void
  {
    this.busyAppointmentId = item.id;
    this.actionErrorText = null;

    this.appointmentsService.cancel(item.id).subscribe({
      next: () =>
      {
        this.busyAppointmentId = null;
        this.loadAppointments({ append: false });
      },
      error: (error: unknown) =>
      {
        console.error('Failed to cancel appointment', error);
        this.busyAppointmentId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected onComplete(item: AppointmentListItem): void
  {
    this.busyAppointmentId = item.id;
    this.actionErrorText = null;

    this.appointmentsService.finish(item.id).subscribe({
      next: () =>
      {
        this.busyAppointmentId = null;
        this.loadAppointments({ append: false });
      },
      error: (error: unknown) =>
      {
        console.error('Failed to complete appointment', error);
        this.busyAppointmentId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected onCheckIn(item: AppointmentListItem): void
  {
    this.busyAppointmentId = item.id;
    this.actionErrorText = null;

    this.appointmentsService.checkIn(item.id).subscribe({
      next: () =>
      {
        this.busyAppointmentId = null;
        this.loadAppointments({ append: false });
      },
      error: (error: unknown) =>
      {
        console.error('Failed to check in appointment', error);
        this.busyAppointmentId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected onNoShow(item: AppointmentListItem): void
  {
    this.busyAppointmentId = item.id;
    this.actionErrorText = null;

    this.appointmentsService.noShow(item.id).subscribe({
      next: () =>
      {
        this.busyAppointmentId = null;
        this.loadAppointments({ append: false });
      },
      error: (error: unknown) =>
      {
        console.error('Failed to mark appointment as no-show', error);
        this.busyAppointmentId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected loadMoreAppointments(): void
  {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreAppointments) {
      return;
    }

    this.loadAppointments({ append: true });
  }

  protected retryAppointments(): void
  {
    this.loadAppointments({ append: false });
  }

  protected refreshAppointments(): void
  {
    this.loadAppointments({ append: false });
  }

  protected openFiltersModal(): void
  {
    this.draftStatus = this.selectedStatus;
    this.draftSort = this.selectedSort;
    this.draftPendingFirst = this.selectedPendingFirst;
    this.draftClinicId = this.selectedClinicFilterId;
    this.draftClientId = this.selectedClientId;
    this.draftDeviceId = this.selectedDeviceIdFilter;
    this.draftSpecialistUserId = this.selectedSpecialistUserIdFilter;
    this.draftProcedureTypeId = this.selectedProcedureTypeIdFilter;
    this.draftClientSearchText = this.clientSearchText;
    this.loadFilterOptions();
    this.isFiltersModalOpen = true;
  }

  protected closeFiltersModal(): void
  {
    this.isFiltersModalOpen = false;
  }

  protected onDraftStatusChange(event: Event): void
  {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    this.draftStatus = this.isAppointmentStatus(value) ? value : '';
  }

  protected onDraftSortChange(event: Event): void
  {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    this.draftSort = this.isAppointmentSort(value) ? value : 'startAtDesc';
  }

  protected onDraftPendingFirstChange(event: Event): void
  {
    this.draftPendingFirst = !!(event.target as HTMLInputElement | null)?.checked;
  }

  protected onDraftClinicChange(event: Event): void
  {
    this.draftClinicId = (event.target as HTMLSelectElement | null)?.value?.trim() ?? '';
  }

  protected onDraftDeviceChange(event: Event): void
  {
    this.draftDeviceId = (event.target as HTMLSelectElement | null)?.value?.trim() ?? '';
  }

  protected onDraftSpecialistChange(event: Event): void
  {
    this.draftSpecialistUserId = (event.target as HTMLSelectElement | null)?.value?.trim() ?? '';
  }

  protected onDraftProcedureChange(event: Event): void
  {
    this.draftProcedureTypeId = (event.target as HTMLSelectElement | null)?.value?.trim() ?? '';
  }

  protected onClientSearchInput(event: Event): void
  {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.draftClientSearchText = value;

    if (!value.trim()) {
      this.clientOptions = [];
      this.draftClientId = '';
      return;
    }

    this.clientSearchTerms.next(value);
  }

  protected onClientSearchCommitted(event: Event): void
  {
    const value = (event.target as HTMLInputElement | null)?.value?.trim() ?? '';
    const matchedClient = this.clientOptions.find((client) =>
      this.clientOptionLabel(client) === value || client.id === value,
    );

    if (matchedClient) {
      this.draftClientId = matchedClient.id;
      this.draftClientSearchText = this.clientOptionLabel(matchedClient);
      return;
    }

    this.draftClientId = this.isUuidLike(value) ? value : '';
  }

  protected clientOptionLabel(client: ClientAdminItem): string
  {
    const parts = [
      client.fullName,
      client.email,
      client.phone,
    ]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0);

    return parts.length > 0 ? parts.join(' · ') : client.id;
  }

  protected applyFiltersModal(): void
  {
    this.updateAppointmentQueryState({
      status: this.draftStatus,
      sort: this.draftSort,
      pendingFirst: this.draftPendingFirst,
      clinicId: this.draftClinicId,
      clientId: this.draftClientId,
      deviceId: this.draftDeviceId,
      specialistUserId: this.draftSpecialistUserId,
      procedureTypeId: this.draftProcedureTypeId,
    });
    this.closeFiltersModal();
  }

  protected resetFiltersModal(): void
  {
    this.draftStatus = '';
    this.draftSort = 'startAtDesc';
    this.draftPendingFirst = true;
    this.draftClinicId = '';
    this.draftClientId = '';
    this.draftClientSearchText = '';
    this.draftDeviceId = '';
    this.draftSpecialistUserId = '';
    this.draftProcedureTypeId = '';
  }

  protected get hasPageActions(): boolean
  {
    return this.canCreateAppointment || !this.isClient;
  }

  private loadAppointments(options: { append: boolean }): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.items = [];
      this.nextCursor = null;
      this.hasMoreAppointments = false;
      this.errorText = 'error.settingsCompanyContextRequired';
      this.actionErrorText = null;
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    if (options.append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
      this.actionErrorText = null;
      this.nextCursor = null;
      this.hasMoreAppointments = false;
    }

    const request$: Observable<AppointmentListPageItem> = this.isClient
      ? this.appointmentsService.getMyPage({
          limit: AppointmentsListPageComponent.PAGE_SIZE,
          cursor: options.append ? this.nextCursor : null,
          sort: this.selectedSort,
          pendingFirst: this.selectedPendingFirst,
          status: this.selectedStatus || undefined,
        })
      : this.appointmentsService.getPage({
          limit: AppointmentsListPageComponent.PAGE_SIZE,
          cursor: options.append ? this.nextCursor : null,
          sort: this.selectedSort,
          pendingFirst: this.selectedPendingFirst,
          filters: {
            status: this.selectedStatus || undefined,
            clinicId: this.selectedClinicFilterId || undefined,
            clientId: this.selectedClientId || undefined,
            deviceId: this.selectedDeviceIdFilter || undefined,
            specialistUserId: this.selectedSpecialistUserIdFilter || undefined,
            procedureTypeId: this.selectedProcedureTypeIdFilter || undefined,
          },
        });

    request$.subscribe({
      next: (response: AppointmentListPageItem) =>
      {
        const nextItems = response.items;

        this.items = options.append ? [...this.items, ...nextItems] : nextItems;
        this.nextCursor = response.nextCursor;
        this.hasMoreAppointments = response.hasMore;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load appointments', error);
        if (options.append) {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenAppointmentsList',
            fallback: 'Failed to load appointments'
          });
          this.isLoadingMore = false;
          return;
        }

        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentsList',
          fallback: 'Failed to load appointments'
        });
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  protected get companyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }

  private applyAppointmentQueryState(params: ParamMap): boolean
  {
    if (this.isClient) {
      return false;
    }

    const statusRaw = params.get('status')?.trim() ?? '';
    const sortRaw = params.get('sort')?.trim() ?? '';
    const clinicIdRaw = params.get('clinicId')?.trim() ?? '';
    const clientIdRaw = params.get('clientId')?.trim() ?? '';
    const deviceIdRaw = params.get('deviceId')?.trim() ?? '';
    const specialistUserIdRaw = params.get('specialistUserId')?.trim() ?? '';
    const procedureTypeIdRaw = params.get('procedureTypeId')?.trim() ?? '';
    const pendingFirstRaw = params.get('pendingFirst')?.trim() ?? '';
    const nextStatus = this.isAppointmentStatus(statusRaw) ? statusRaw : '';
    const nextSort = this.isAppointmentSort(sortRaw) ? sortRaw : 'startAtDesc';
    const nextPendingFirst = pendingFirstRaw === '' ? true : pendingFirstRaw === 'true' || pendingFirstRaw === '1';
    const changed = this.selectedStatus !== nextStatus
      || this.selectedSort !== nextSort
      || this.selectedPendingFirst !== nextPendingFirst
      || this.selectedClinicFilterId !== clinicIdRaw
      || this.selectedClientId !== clientIdRaw
      || this.selectedDeviceIdFilter !== deviceIdRaw
      || this.selectedSpecialistUserIdFilter !== specialistUserIdRaw
      || this.selectedProcedureTypeIdFilter !== procedureTypeIdRaw;

    this.selectedStatus = nextStatus;
    this.selectedSort = nextSort;
    this.selectedPendingFirst = nextPendingFirst;
    this.selectedClinicFilterId = clinicIdRaw;
    this.selectedClientId = clientIdRaw;
    this.selectedDeviceIdFilter = deviceIdRaw;
    this.selectedSpecialistUserIdFilter = specialistUserIdRaw;
    this.selectedProcedureTypeIdFilter = procedureTypeIdRaw;
    this.syncClientSearchText(clientIdRaw);

    return changed;
  }

  private applyAppointmentQuickViewQueryState(params: ParamMap): void
  {
    if (params.get('editAppointmentId')?.trim()) {
      this.isAppointmentQuickViewOpen = false;
      this.selectedAppointmentQuickViewId = null;
      this.selectedAppointmentClientViewMode = false;
      return;
    }

    const appointmentId = params.get('appointmentId')?.trim() ?? '';
    if (!appointmentId) {
      this.isAppointmentQuickViewOpen = false;
      this.selectedAppointmentQuickViewId = null;
      this.selectedAppointmentClientViewMode = false;
      return;
    }

    this.selectedAppointmentQuickViewId = appointmentId;
    this.selectedAppointmentClientViewMode = params.get('clientView') === '1';
    this.isAppointmentQuickViewOpen = true;
  }

  private applyAppointmentEditQueryState(params: ParamMap): void
  {
    const appointmentId = params.get('editAppointmentId')?.trim() ?? '';
    if (!appointmentId) {
      return;
    }

    this.isAppointmentQuickViewOpen = false;
    this.selectedAppointmentQuickViewId = null;
    this.selectedAppointmentClientViewMode = false;
    this.editAppointmentId = appointmentId;
    this.isEditAppointmentModalOpen = true;
  }

  private clearAppointmentQuickViewQueryParam(): void
  {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        appointmentId: null,
        clientView: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clearAppointmentEditQueryParam(): void
  {
    if (!this.route.snapshot.queryParamMap.has('editAppointmentId')) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        editAppointmentId: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private updateAppointmentQueryState(nextState: {
    status: '' | AppointmentStatus;
    sort: AppointmentListSort;
    pendingFirst: boolean;
    clinicId: string;
    clientId: string;
    deviceId: string;
    specialistUserId: string;
    procedureTypeId: string;
  }): void
  {
    if (this.isClient) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        status: nextState.status || null,
        sort: nextState.sort === 'startAtDesc' ? null : nextState.sort,
        pendingFirst: nextState.pendingFirst ? null : 'false',
        clinicId: nextState.clinicId || null,
        clientId: nextState.clientId || null,
        deviceId: nextState.deviceId || null,
        specialistUserId: nextState.specialistUserId || null,
        procedureTypeId: nextState.procedureTypeId || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private loadFilterOptions(): void
  {
    const companyId = this.getContextCompanyId();
    const cacheKey = companyId ?? 'current-company';

    if (this.filterOptionsLoadedForCompanyId === cacheKey || this.filterOptionsLoading) {
      return;
    }

    if (!this.hasRequiredCompanyContext()) {
      this.clearFilterOptions();
      return;
    }

    this.filterOptionsLoading = true;
    this.subscriptions.add(
      forkJoin({
        clinics: this.settingsService.getClinicOperationalSettings(companyId ?? undefined).pipe(
          catchError(() => of([] as ClinicOperationalSettingsDto[])),
        ),
        devices: this.devicesService.getList(companyId ?? undefined).pipe(
          catchError(() => of([] as DeviceItem[])),
        ),
        specialists: this.usersService.getList(companyId ?? undefined).pipe(
          catchError(() => of([] as UserItem[])),
        ),
        procedures: this.procedureTypesService.getList({
          includeInactive: true,
          companyId: companyId ?? undefined,
        }).pipe(
          catchError(() => of([] as ProcedureTypeItem[])),
        ),
      }).subscribe(({ clinics, devices, specialists, procedures }) => {
        this.clinicOptions = clinics.map((clinic) => ({
          id: clinic.clinicId,
          label: clinic.clinicName,
        }));
        this.deviceOptions = devices.map((device) => ({
          id: device.id,
          label: this.deviceOptionLabel(device),
        }));
        this.specialistOptions = specialists
          .filter((user) => user.role === 'specialist')
          .map((user) => ({
            id: user.id,
            label: user.fullName.trim() || user.email.trim() || user.id,
          }));
        this.procedureTypeOptions = procedures.map((procedure) => ({
          id: procedure.id,
          label: procedure.name,
        }));
        this.filterOptionsLoadedForCompanyId = cacheKey;
        this.filterOptionsLoading = false;
      }),
    );
  }

  private clearFilterOptions(): void
  {
    this.clinicOptions = [];
    this.deviceOptions = [];
    this.specialistOptions = [];
    this.procedureTypeOptions = [];
    this.filterOptionsLoadedForCompanyId = null;
    this.filterOptionsLoading = false;
  }

  private bindClientSearch(): void
  {
    this.subscriptions.add(
      this.clientSearchTerms.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const query = term.trim();
          if (query.length < 2 || !this.hasRequiredCompanyContext()) {
            return of([] as ClientAdminItem[]);
          }

          return this.clientsAdminService.getPage({
            companyId: this.getContextCompanyId() ?? undefined,
            limit: AppointmentsListPageComponent.CLIENT_SEARCH_LIMIT,
            query,
          }).pipe(
            switchMap((page) => of(page.items)),
            catchError(() => of([] as ClientAdminItem[])),
          );
        }),
      ).subscribe((items) => {
        this.clientOptions = items;
      }),
    );
  }

  private syncClientSearchText(clientId: string): void
  {
    if (!clientId) {
      this.clientSearchText = '';
      if (!this.isFiltersModalOpen) {
        this.draftClientSearchText = '';
      }
      return;
    }

    const existing = this.clientOptions.find((client) => client.id === clientId);
    if (existing) {
      this.clientSearchText = this.clientOptionLabel(existing);
      if (!this.isFiltersModalOpen) {
        this.draftClientSearchText = this.clientSearchText;
      }
      return;
    }

    this.clientSearchText = clientId;
    if (!this.isFiltersModalOpen) {
      this.draftClientSearchText = clientId;
    }

    this.clientsAdminService.getById(
      clientId,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (client) => {
        if (this.selectedClientId !== client.id) {
          return;
        }

        this.clientOptions = [
          client,
          ...this.clientOptions.filter((item) => item.id !== client.id),
        ];
        this.clientSearchText = this.clientOptionLabel(client);
        if (!this.isFiltersModalOpen) {
          this.draftClientSearchText = this.clientSearchText;
        }
      },
      error: () => {},
    });
  }

  private getContextCompanyId(): string | null
  {
    return this.isSystemAdmin
      ? this.systemAdminContext.getCompanyId()?.trim() || null
      : null;
  }

  private deviceOptionLabel(device: DeviceItem): string
  {
    return [device.brand, device.model, device.serialNumber]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0)
      .join(' / ')
      || device.id;
  }

  private isUuidLike(value: string): boolean
  {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{12}$/i.test(value);
  }

  private isAppointmentStatus(value: string): value is AppointmentStatus
  {
    return [
      'pending_confirmation',
      'confirmed',
      'completed',
      'cancelled',
      'reschedule_required',
      'no_show',
    ].includes(value);
  }

  private isAppointmentSort(value: string): value is AppointmentListSort
  {
    return [
      'createdAtDesc',
      'createdAtAsc',
      'startAtAsc',
      'startAtDesc',
    ].includes(value);
  }

}
